import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Loader2, Trash2, Pencil } from "lucide-react";

// config: { key, listEndpoint, writeEndpoint, fields:[{key,label,type,placeholder}], primary:[keys], secondary:(item)=>string }
export function AdminCrud({ config }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const empty = config.fields.reduce((a, f) => ({ ...a, [f.key]: f.type === "checkbox" ? true : f.type === "number" ? "" : "" }), {});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(config.listEndpoint);
      setItems(data);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [config.listEndpoint]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setShowForm(true); };
  const openEdit = (it) => { setForm({ ...empty, ...it }); setEditId(it.id); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      config.fields.forEach((f) => {
        let v = form[f.key];
        if (f.type === "number") v = parseFloat(v) || 0;
        payload[f.key] = v;
      });
      if (editId) {
        await api.put(`${config.writeEndpoint}/${editId}`, payload);
      } else {
        await api.post(config.writeEndpoint, payload);
      }
      toast.success("Saved");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`${config.writeEndpoint}/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={`${config.key}-panel`}>
      <div className="flex justify-between items-center mb-6">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">{items.length} {config.key}</span>
        <button data-testid={`add-${config.key}-btn`} onClick={openNew} className="flex items-center gap-2 bg-neon text-void font-bold uppercase tracking-[0.15em] text-xs py-3 px-5 hover:bg-[#FAFAFA] transition-colors">
          <Plus size={16} /> Add
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 font-mono text-sm text-[#9CA3AF]"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`${config.key}-row-${it.id}`} className="border border-[#222] bg-matte p-4 flex items-center gap-4">
              {it.image_url && <img src={it.image_url} alt="" className="w-14 h-14 object-cover grayscale border border-[#222] shrink-0" />}
              <div className="flex-1 min-w-0">
                <div title={config.primary.map((k) => it[k]).filter(Boolean).join(" · ")} className="font-grotesk font-bold text-[#FAFAFA] truncate">{config.primary.map((k) => it[k]).filter(Boolean).join(" · ")}</div>
                <div title={config.secondary(it)} className="font-mono text-xs text-[#9CA3AF] truncate mt-1">{config.secondary(it)}</div>
              </div>
              <button data-testid={`edit-${config.key}-${it.id}`} onClick={() => openEdit(it)} className="border border-[#333] text-xs uppercase tracking-widest px-3 py-2 hover:border-neon hover:text-neon transition-colors flex items-center gap-1"><Pencil size={13} /> Edit</button>
              <button data-testid={`delete-${config.key}-${it.id}`} onClick={() => del(it.id)} className="border border-[#333] text-[#9CA3AF] hover:text-red-400 hover:border-red-900 transition-colors px-3 py-2"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-void/80 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid={`${config.key}-form`} className="w-full max-w-lg bg-matte border border-[#222] max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-[#222] flex items-center justify-between">
              <span className="font-display uppercase text-2xl tracking-tight">{editId ? "Edit" : "Add"} {config.singular}</span>
              <button type="button" onClick={() => setShowForm(false)}><X className="text-[#9CA3AF] hover:text-neon" /></button>
            </div>
            <div className="p-5 space-y-4">
              {config.fields.map((f) => (
                <label key={f.key} className="block">
                  <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-1">{f.label}</span>
                  {f.type === "textarea" ? (
                    <textarea data-testid={`field-${f.key}`} rows={3} value={form[f.key] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} placeholder={f.placeholder} className="w-full bg-void border border-[#222] px-3 py-2 font-mono text-sm outline-none focus:border-neon focus:ring-1 focus:ring-neon rounded-none" />
                  ) : f.type === "checkbox" ? (
                    <input data-testid={`field-${f.key}`} type="checkbox" checked={!!form[f.key]} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.checked }))} className="w-5 h-5 accent-[#98FF56]" />
                  ) : (
                    <input data-testid={`field-${f.key}`} type={f.type === "number" ? "number" : "text"} step="any" value={form[f.key] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} placeholder={f.placeholder} className="w-full bg-void border border-[#222] px-3 py-2 font-mono text-sm outline-none focus:border-neon focus:ring-1 focus:ring-neon rounded-none" />
                  )}
                </label>
              ))}
            </div>
            <div className="p-5 border-t border-[#222]">
              <button data-testid={`save-${config.key}-btn`} disabled={saving} className="w-full bg-neon text-void font-bold uppercase tracking-[0.2em] text-sm py-3 flex items-center justify-center gap-2 hover:bg-[#FAFAFA] transition-colors disabled:opacity-50">
                {saving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
